if (!customElements.get('sa-customize-look')) {
  class SaCustomizeLook extends HTMLElement {
    connectedCallback() {
      this.discountPercent = Number.parseFloat(this.dataset.discountPercent) || 0;
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.bundleTitle = this.dataset.parentProductTitle || 'Bundle';
      this.parentUrl = this.dataset.parentUrl || '';
      this.parentImage = this.dataset.parentImage || '';
      this.parentVariantId = this.getParentVariantId();
      this.data = this.readData();
      this.hiddenOptions = this.data.hiddenOptions || [];
      this.variantMap = this.buildVariantMap();
      this.items = Array.from(this.querySelectorAll('[data-sa-customize-item]'));
      this.totalsEl = this.querySelector('.sa-customize-look__totals');
      this.addButton = this.querySelector('[data-sa-customize-add]');
      this.addButtonText = this.querySelector('[data-sa-customize-add-text]');
      this.errorEl = this.querySelector('[data-sa-customize-error]');
      this.originalTotalEl = this.querySelector('[data-sa-customize-original-total]');
      this.discountedTotalEl = this.querySelector('[data-sa-customize-discounted-total]');

      this.bindVariantSelects();
      this.bindItems();
      this.addButton?.addEventListener('click', () => this.addToCart());
      this.querySelector('[data-sa-customize-live-chat]')?.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof window.openChat === 'function') window.openChat();
      });

      this.updateTotals();
    }

    getParentVariantId() {
      return this.dataset.cartVariantId || this.dataset.parentVariantId || '';
    }

    readData() {
      const script = this.querySelector('[data-sa-customize-data]');
      if (!script) return { hiddenOptions: [], products: [] };

      try {
        return JSON.parse(script.textContent);
      } catch {
        return { hiddenOptions: [], products: [] };
      }
    }

    buildVariantMap() {
      const map = new Map();

      (this.data.products || []).forEach((product) => {
        (product.variants || []).forEach((variant) => {
          map.set(String(variant.id), { ...variant, productId: product.id, productTitle: product.title });
        });
      });

      return map;
    }

    bindVariantSelects() {
      this.items.forEach((item) => {
        const root = item.querySelector('[data-sa-customize-variant-root]');
        const select = item.querySelector('[data-sa-customize-variant]');
        if (!root || !select || root.dataset.saVariantInit === 'true') return;

        root.dataset.saVariantInit = 'true';

        const trigger = root.querySelector('[data-sa-customize-variant-trigger]');
        const label = root.querySelector('[data-sa-customize-variant-label]');
        const menu = root.querySelector('[data-sa-customize-variant-menu]');
        if (!trigger || !label || !menu) return;

        const closeMenu = () => {
          menu.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          root.classList.remove('is-open');
        };

        const openMenu = () => {
          this.querySelectorAll('[data-sa-customize-variant-root].is-open').forEach((openRoot) => {
            if (openRoot === root) return;
            openRoot.classList.remove('is-open');
            openRoot.querySelector('[data-sa-customize-variant-menu]').hidden = true;
            openRoot
              .querySelector('[data-sa-customize-variant-trigger]')
              ?.setAttribute('aria-expanded', 'false');
          });

          menu.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          root.classList.add('is-open');
        };

        const syncLabel = () => {
          const selected = select.options[select.selectedIndex];
          label.textContent = selected?.textContent?.trim() || '';

          menu.querySelectorAll('[data-sa-customize-variant-option]').forEach((optionEl) => {
            const isSelected = optionEl.dataset.value === select.value;
            optionEl.classList.toggle('is-selected', isSelected);
            optionEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          });
        };

        const buildMenu = () => {
          menu.innerHTML = '';

          Array.from(select.options).forEach((option) => {
            if (option.disabled) return;

            const optionEl = document.createElement('li');
            optionEl.className = 'sa-customize-look__variant-option';
            optionEl.dataset.value = option.value;
            optionEl.dataset.saCustomizeVariantOption = '';
            optionEl.setAttribute('role', 'option');
            optionEl.textContent = option.textContent;
            optionEl.tabIndex = -1;

            optionEl.addEventListener('click', (event) => {
              event.stopPropagation();
              if (select.value !== option.value) {
                select.value = option.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              syncLabel();
              closeMenu();
            });

            menu.appendChild(optionEl);
          });

          syncLabel();
        };

        buildMenu();

        trigger.addEventListener('click', (event) => {
          event.stopPropagation();
          if (menu.hidden) openMenu();
          else closeMenu();
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (menu.hidden) openMenu();
          }

          if (event.key === 'Escape') {
            closeMenu();
          }
        });

        select.addEventListener('change', syncLabel);
      });

      if (this._variantOutsideClickBound) return;

      this._variantOutsideClickBound = true;
      document.addEventListener('click', () => {
        this.querySelectorAll('[data-sa-customize-variant-root].is-open').forEach((root) => {
          root.classList.remove('is-open');
          root.querySelector('[data-sa-customize-variant-menu]').hidden = true;
          root.querySelector('[data-sa-customize-variant-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      });
    }

    bindItems() {
      this.items.forEach((item) => {
        const variantControl = item.querySelector('[data-sa-customize-variant]');
        const qtyInput = item.querySelector('[data-sa-customize-qty]');
        const minusBtn = item.querySelector('[data-sa-customize-qty-minus]');
        const plusBtn = item.querySelector('[data-sa-customize-qty-plus]');

        const syncQtyButtons = () => {
          const qty = Number.parseInt(qtyInput?.value, 10) || 0;
          if (minusBtn) {
            minusBtn.classList.toggle('is-active', qty > 0);
            minusBtn.disabled = false;
          }
        };

        const updateLinePrice = () => {
          const variant = this.variantMap.get(String(variantControl?.value));
          const linePriceEl = item.querySelector('[data-sa-customize-line-price]');
          const lineCompareEl = item.querySelector('[data-sa-customize-line-compare]');

          if (!variant) return;

          const skuEl = item.querySelector('[data-sa-customize-sku]');
          if (skuEl) {
            const sku = variant.sku || '';
            skuEl.textContent = sku ? `${sku}` : '';
            skuEl.classList.toggle('hidden', !sku);
          }

          if (!linePriceEl) return;

          const { display, compare } = this.getLineUnitPrices(variant);

          linePriceEl.textContent = this.formatMoney(display);

          if (lineCompareEl) {
            const showCompare = Boolean(compare);
            lineCompareEl.classList.toggle('hidden', !showCompare);
            if (showCompare) {
              lineCompareEl.textContent = this.formatMoney(compare);
            }
          }
        };

        variantControl?.addEventListener('change', () => {
          updateLinePrice();
          this.updateTotals();
        });

        minusBtn?.addEventListener('click', () => {
          const current = Number.parseInt(qtyInput.value, 10) || 0;
          qtyInput.value = String(Math.max(0, current - 1));
          syncQtyButtons();
          updateLinePrice();
          this.updateTotals();
        });

        plusBtn?.addEventListener('click', () => {
          const current = Number.parseInt(qtyInput.value, 10) || 0;
          qtyInput.value = String(current + 1);
          syncQtyButtons();
          updateLinePrice();
          this.updateTotals();
        });

        qtyInput?.addEventListener('change', () => {
          let qty = Number.parseInt(qtyInput.value, 10);
          if (Number.isNaN(qty) || qty < 0) qty = 0;
          qtyInput.value = String(qty);
          syncQtyButtons();
          updateLinePrice();
          this.updateTotals();
        });

        syncQtyButtons();
        updateLinePrice();
      });
    }

    collectSelectedItems() {
      const selected = [];

      this.items.forEach((item) => {
        const variantId = item.querySelector('[data-sa-customize-variant]')?.value;
        const qty = Number.parseInt(item.querySelector('[data-sa-customize-qty]')?.value, 10) || 0;
        const variant = this.variantMap.get(String(variantId));

        if (!variantId || qty <= 0 || !variant?.available) return;

        selected.push({
          id: Number.parseInt(variantId, 10),
          quantity: qty,
          variant,
          title: item.dataset.productTitle || variant.productTitle || '',
        });
      });

      return selected;
    }

    calculateTotals(selected) {
      let originalTotal = 0;
      let currentTotal = 0;

      selected.forEach(({ quantity, variant }) => {
        const unitPrice = variant.price || 0;
        const unitOriginal =
          variant.compare_at_price && variant.compare_at_price > unitPrice
            ? variant.compare_at_price
            : unitPrice;

        originalTotal += unitOriginal * quantity;
        currentTotal += unitPrice * quantity;
      });

      const discountMultiplier = Math.max(0, 1 - this.discountPercent / 100);
      const discountedTotal = this.hasBundleDiscountDisplay()
        ? Math.round(currentTotal * discountMultiplier)
        : currentTotal;

      return { originalTotal, currentTotal, discountedTotal };
    }

    buildComponentSummary(selected) {
      const summary = selected
        .map(({ quantity, variant, title }) => {
          const variantLabel =
            variant.title && variant.title !== 'Default Title' ? ` — ${variant.title}` : '';
          return `${quantity} × ${title}${variantLabel}`;
        })
        .join('; ');

      return {
        summary,
        count: selected.length,
      };
    }

    hasBundleDiscount() {
      return this.discountPercent > 0 && Boolean(this.discountCode);
    }

    hasBundleDiscountDisplay() {
      return this.discountPercent > 0;
    }

    getLineUnitPrices(variant) {
      const unitPrice = variant.price || 0;
      const unitCompare =
        variant.compare_at_price && variant.compare_at_price > unitPrice
          ? variant.compare_at_price
          : null;

      if (this.hasBundleDiscountDisplay()) {
        const multiplier = Math.max(0, 1 - this.discountPercent / 100);
        return {
          display: Math.round(unitPrice * multiplier),
          compare: unitPrice,
        };
      }

      return {
        display: unitPrice,
        compare: unitCompare,
      };
    }

    buildComponentProperties(bundleId, baseQuantity, meta) {
      const props = {
        _bundle_id: bundleId,
        _bundle_title: this.bundleTitle,
        _bundle_role: 'component',
        _bundle_base_quantity: String(baseQuantity),
        _bundle_parent_variant_id: String(this.getParentVariantId()),
        _bundle_parent_url: this.parentUrl,
        _bundle_parent_image: this.parentImage,
      };

      if (this.hasBundleDiscount()) {
        props._bundle_discount_percent = String(this.discountPercent);
        props._bundle_discount_code = this.discountCode;
      }

      if (meta) {
        props._bundle_item_count = String(meta.count);
        props._bundle_variant_summary = meta.summary;
      }

      return props;
    }

    buildCartItems(selected, bundleId) {
      const meta = this.buildComponentSummary(selected);

      return selected.map(({ id, quantity }, index) => ({
        id,
        quantity,
        properties: this.buildComponentProperties(bundleId, quantity, index === 0 ? meta : null),
      }));
    }

    getCartJsonUrl(routeUrl, fallbackPath) {
      const url = routeUrl || fallbackPath;
      return url.endsWith('.js') ? url : `${url}.js`;
    }

    async parseJsonResponse(response) {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        throw new Error(
          text.includes('<!DOCTYPE')
            ? 'Cart request failed. Please refresh the page and try again.'
            : 'Invalid response from cart API.',
        );
      }
    }

    createBundleId() {
      return `bundle-${this.dataset.sectionId}-${Date.now()}`;
    }

    getCartSections() {
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    async addToCart() {
      const selected = this.collectSelectedItems();
      if (!selected.length) {
        this.setError('Please select at least one item.');
        return;
      }

      if (!this.getParentVariantId()) {
        this.setError('Bundle display variant is not configured. Set custom.bundle_cart_variant on this product.');
        return;
      }

      if (this.discountPercent > 0 && !this.discountCode) {
        this.setError(
          '请在 Theme Editor 配置 Bundle discount code，并在 Shopify 后台创建与之匹配的商品级自动折扣（需读取行项目 _bundle_discount_percent），Checkout 才会按 Bundle 折扣结算。',
        );
        return;
      }

      const cartApi = window.SaCartApi;
      if (!cartApi) {
        this.setError('Cart API is unavailable. Please refresh the page.');
        return;
      }

      const bundleId = this.createBundleId();
      const cartItems = this.buildCartItems(selected, bundleId);
      const cartAddUrl = this.getCartJsonUrl(window.routes?.cart_add_url, '/cart/add');
      const sections = this.getCartSections();

      this.setError('');
      this.setLoading(true);

      let addSucceeded = false;

      try {
        const data = await cartApi.enqueue(async () => {
          const response = await cartApi.fetchWithRetry(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: cartItems,
              sections,
              sections_url: window.location.pathname,
            }),
          });

          const payload = await cartApi.parseJsonResponse(response);

          if (!response.ok || payload.status) {
            throw new Error(payload.description || payload.message || 'Add to cart failed');
          }

          return payload;
        });

        addSucceeded = true;

        const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cartUi?.renderContents) {
          cartUi.renderContents(data);
        } else if (cartUi?.open) {
          cartUi.open();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sa-customize-look',
            cartData: data,
          });
        }

        if (this.addButtonText) {
          const original = this.addButtonText.textContent;
          this.addButtonText.textContent = 'Added';
          window.setTimeout(() => {
            this.addButtonText.textContent = original;
          }, 1500);
        }
      } catch (error) {
        if (addSucceeded) {
          const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          cartUi?.open?.();
        }
        if (!this.errorEl?.textContent) {
          this.setError(error.message || 'Unable to add items to cart.');
        }
      } finally {
        this.setLoading(false);
        this.updateTotals();
      }
    }

    updateTotals() {
      const selected = this.collectSelectedItems();
      const totals = this.calculateTotals(selected);
      const hasSelection = selected.length > 0;

      if (this.totalsEl) {
        this.totalsEl.dataset.saHasSelection = hasSelection ? 'true' : 'false';
      }

      if (this.originalTotalEl) {
        this.originalTotalEl.textContent = this.formatMoney(hasSelection ? totals.originalTotal : 0);
        this.originalTotalEl.classList.toggle(
          'line-through',
          hasSelection && totals.originalTotal > totals.discountedTotal,
        );
      }

      if (this.discountedTotalEl) {
        this.discountedTotalEl.textContent = this.formatMoney(hasSelection ? totals.discountedTotal : 0);
      }

      // if (this.addButton) {
      //   this.addButton.disabled = !hasSelection;
      // }

      if (hasSelection && this.errorEl?.textContent === 'Please select at least one item.') {
        this.setError('');
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      if (!message) {
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
        return;
      }

      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }

    setLoading(loading) {
      if (!this.addButton) return;
      this.addButton.classList.toggle('loading', loading);
      this.addButton.disabled = loading || this.collectSelectedItems().length === 0;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        const format =
          window.theme?.moneyFormat || window.Shopify.theme?.moneyFormat || '${{amount}}';
        return Shopify.formatMoney(cents, format);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  customElements.define('sa-customize-look', SaCustomizeLook);
}
